import * as React from 'react';
import { cn } from '../lib/utils';

export interface CountBadgeProps extends Omit<React.ComponentPropsWithoutRef<'span'>, 'children'> {
  /** The count. Nothing renders for 0, or while it is unknown (null/undefined). */
  count: number | null | undefined;
  /**
   * attention: things that need the team (amber). neutral: ordinary volume,
   * such as unread or total counts.
   */
  tone?: 'neutral' | 'attention';
  /** Read after the number by screen readers, e.g. "need you". */
  label?: string;
  /** Render a zero instead of nothing. */
  showZero?: boolean;
  /** Counts above this show as `max+`. */
  max?: number;
}

/**
 * A count beside a label: in navigation, tabs and filters. An unavailable
 * count is never shown as zero, and a zero is quiet unless asked for.
 */
export function CountBadge({
  count,
  tone = 'neutral',
  label,
  showZero = false,
  max = 99,
  className,
  ...props
}: CountBadgeProps) {
  if (count === null || count === undefined || (count === 0 && !showZero)) return null;
  const shown = count > max ? `${max}+` : count.toLocaleString();
  return (
    <span
      className={cn(
        'inline-flex h-[1.125rem] min-w-[1.125rem] shrink-0 items-center justify-center rounded-full px-1.5 text-caption font-semibold tabular-nums',
        tone === 'attention' ? 'bg-warning-muted text-warning' : 'bg-emphasis text-muted-foreground',
        className
      )}
      {...props}
    >
      {shown}
      {label ? <span className="sr-only"> {label}</span> : null}
    </span>
  );
}
